// src/pages/admin/AdminShifts.jsx
// ============================================================
// Admin page for Shift Management.
// - Define shifts (morning, evening, night, custom)
// - Assign shifts to employees
// - View all employee shift assignments
// ============================================================

import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useShifts,
  useAllShiftAssignments,
  usePendingShiftRequests,
  createShift,
  updateShift,
  deleteShift,
  assignShift,
  removeShiftAssignment,
  reviewShiftAssignment,
  seedStandardShifts,
} from "../../hooks/useFirestoreExtensions";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAllUsers } from "../../hooks/useFirestore";
import { ADMIN_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

const SHIFT_TYPES = [
  { 
    value: "GENERAL", 
    label: "General Shift", 
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ), 
    color: "blue" 
  },
  { 
    value: "MORNING", 
    label: "Morning Shift", 
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
      </svg>
    ), 
    color: "amber" 
  },
  {
    value: "EVENING",
    label: "Evening Shift",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="18" height="18">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 0 0 4.5 4.5H18a3.75 3.75 0 0 0 1.332-7.257 3 3 0 0 0-3.758-3.848 5.25 5.25 0 0 0-10.233 2.33A4.502 4.502 0 0 0 2.25 15Z" />
      </svg>
    ),
    color: "indigo",
  },
  {
    value: "NIGHT",
    label: "Night Shift",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="18" height="18">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
      </svg>
    ),
    color: "purple",
  }
];

// Default times for each type
const DEFAULT_TIMES = {
  GENERAL: { start: "09:00", end: "18:00" },
  MORNING: { start: "06:00", end: "15:00" },
  EVENING: { start: "14:00", end: "23:00" },
  NIGHT: { start: "22:00", end: "07:00" },
};

function cleanShiftName(name, start, end) {
  if (!name) return "";
  const lower = name.toLowerCase();
  if (lower.startsWith("gen") && !lower.startsWith("general")) {
    return "General Shift";
  }
  if (start && end) {
    const sClean = start.replace(/\s*[AP]M\s*/gi, "").trim();
    const eClean = end.replace(/\s*[AP]M\s*/gi, "").trim();
    if (
      (sClean === "09:00" || sClean === "9:00") &&
      (eClean === "18:00" || eClean === "6:00" || eClean === "06:00") &&
      name !== "Admin Working Hours"
    ) {
      return "General Shift";
    }
  }
  return name;
}

// ── Duration calculation ──────────────────────────────────
function calcDuration(start, end) {
  if (!start || !end) return "";
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 1440; // overnight
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m ? ` ${m}m` : ""}`;
}

// ── Format 24h → 12h ─────────────────────────────────────
function fmt12(time24) {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function AdminShifts() {
  const { userProfile } = useAuth();
  const { data: shifts, loading: sLoad } = useShifts();
  const { data: assignments, loading: aLoad } = useAllShiftAssignments();
  const { data: users, loading: uLoad } = useAllUsers();
  const { data: pendingRequests, loading: prLoad } = usePendingShiftRequests();

  const [localAssignments, setLocalAssignments] = useState([]);
  const [activeTab, setActiveTab] = useState("shifts"); // "shifts" | "assign" | "requests"

  // Shift form
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [editShift, setEditShift] = useState(null);
  const [shiftForm, setShiftForm] = useState({
    name: "",
    type: "GENERAL",
    startTime: "09:00",
    endTime: "18:00",
    description: "",
  });
  const [shiftError, setShiftError] = useState("");
  const [shiftSaving, setShiftSaving] = useState(false);
  const [deletingShift, setDeletingShift] = useState({});

  // Assign form
  const [assignState, setAssignState] = useState({}); // { userId: shiftId }
  const [assigning, setAssigning] = useState({});
  const [assignMsg, setAssignMsg] = useState(""); // success feedback
  const [assignError, setAssignError] = useState(""); // error feedback

  // Shift Report Modal
  const [selectedShiftForModal, setSelectedShiftForModal] = useState(null);

  useEffect(() => {
    setLocalAssignments(assignments);
  }, [assignments]);

  // Seed the 4 standard shifts on first admin load (idempotent)
  useEffect(() => {
    if (userProfile?.uid) {
      seedStandardShifts(userProfile.uid).catch((err) =>
        console.warn("[AdminShifts] seedStandardShifts:", err)
      );
    }
  }, [userProfile?.uid]);

  // Admin can only assign to Team Leads
  const leadsOnly = useMemo(
    () => users.filter((u) => u.role === "lead"),
    [users],
  );

  // ── Report computations ─────────────────────────────────
  const allActiveUsers = useMemo(
    () => users.filter((u) => u.isActive !== false),
    [users],
  );

  // Assignment map: userId → assignment doc
  const assignedCount = useMemo(
    () =>
      allActiveUsers.filter((u) => {
        const active = localAssignments.find(
          (a) => a.userId === u.uid && a.status?.toUpperCase() === "APPROVED"
        );
        return !!active;
      }).length,
    [localAssignments, allActiveUsers],
  );

  const approvedAssignmentMap = useMemo(() => {
    const map = {};
    localAssignments
      .filter((a) => a.status?.toUpperCase() === "APPROVED")
      .forEach((a) => {
        const isActiveUser = allActiveUsers.some((u) => u.uid === a.userId);
        if (isActiveUser) {
          map[a.userId] = a;
        }
      });
    return map;
  }, [localAssignments, allActiveUsers]);
  const shiftWiseCounts = useMemo(() => {
    const counts = {};
    shifts.forEach((s) => { counts[s.id] = 0; });
    Object.values(approvedAssignmentMap).forEach((a) => {
      if (counts[a.shiftId] !== undefined) counts[a.shiftId]++;
      else counts[a.shiftId] = 1;
    });
    return counts;
  }, [shifts, approvedAssignmentMap]);
  const assignedUsersList = useMemo(
    () => allActiveUsers.filter((u) => approvedAssignmentMap[u.uid]),
    [allActiveUsers, approvedAssignmentMap],
  );
  const unassignedUsersList = useMemo(
    () => allActiveUsers.filter((u) => !approvedAssignmentMap[u.uid]),
    [allActiveUsers, approvedAssignmentMap],
  );

  // ── Shift form handlers ─────────────────────────────────
  function openAddShift() {
    setEditShift(null);
    setShiftForm({
      name: "",
      type: "morning",
      startTime: "09:00",
      endTime: "18:00",
      description: "",
    });
    setShiftError("");
    setShowShiftForm(true);
  }

  function openEditShift(shift) {
    setEditShift(shift);
    setShiftForm({
      name: shift.name,
      type: shift.type || "custom",
      startTime: shift.startTime,
      endTime: shift.endTime,
      description: shift.description || "",
    });
    setShiftError("");
    setShowShiftForm(true);
  }

  function handleTypeChange(type) {
    const times = DEFAULT_TIMES[type] || DEFAULT_TIMES.custom;
    const autoName = (SHIFT_TYPES.find((t) => t.value === type)?.label || type) + " Shift";
    setShiftForm((f) => ({
      ...f,
      type,
      startTime: times.start,
      endTime: times.end,
      // Always auto-update the name when type changes so it stays in sync
      name: autoName,
    }));
  }

  async function handleShiftSubmit() {
    if (!shiftForm.name.trim() || !shiftForm.startTime || !shiftForm.endTime) {
      setShiftError("Name, start time and end time are required.");
      return;
    }
    // Client-side duplicate timing check
    const isDuplicateTiming = shifts.some(
      (s) =>
        s.startTime === shiftForm.startTime &&
        s.endTime === shiftForm.endTime &&
        (!editShift || s.id !== editShift.id),
    );
    if (isDuplicateTiming) {
      setShiftError("❌ Shift already exists for selected timings.");
      return;
    }
    setShiftSaving(true);
    setShiftError("");
    try {
      if (editShift) {
        await updateShift(editShift.id, {
          name: shiftForm.name.trim(),
          type: shiftForm.type,
          startTime: shiftForm.startTime,
          endTime: shiftForm.endTime,
          description: shiftForm.description,
        });
      } else {
        await createShift({
          name: shiftForm.name.trim(),
          type: shiftForm.type,
          startTime: shiftForm.startTime,
          endTime: shiftForm.endTime,
          description: shiftForm.description,
          createdBy: userProfile?.uid,
        });
      }
      setShowShiftForm(false);
    } catch (err) {
      setShiftError(err.message || "Failed to save shift.");
    }
    setShiftSaving(false);
  }

  async function handleDeleteShift(id) {
    if (
      !window.confirm(
        "Delete this shift? Existing assignments will show 'Unknown Shift'.",
      )
    )
      return;
    setDeletingShift((d) => ({ ...d, [id]: true }));
    try {
      await deleteShift(id);
    } catch (err) {
      console.error(err);
    }
    setDeletingShift((d) => ({ ...d, [id]: false }));
  }

  // ── Assign handler ──────────────────────────────────────
  async function handleAssign(userId, userName, employeeId) {
    const shiftId = assignState[userId];
    if (!shiftId) return;

    const shift = shifts.find((s) => s.id === shiftId);
    if (!shift) return;

    setAssigning((a) => ({ ...a, [userId]: true }));
    setAssignMsg("");
    setAssignError("");

    try {
      if (!userProfile?.uid) {
        throw new Error("Admin session not found. Please log in again.");
      }

      const adminRef = doc(db, "users", userProfile.uid);
      const adminSnap = await getDoc(adminRef);
      if (!adminSnap.exists()) {
        throw new Error("Admin profile not found in database.");
      }
      const adminData = adminSnap.data();
      if (adminData.role !== "admin") {
        throw new Error("Unauthorized. Only administrators can assign shifts.");
      }

      // Safe fallback admin mapping
      const adminIdentifier = adminData.fullName || adminData.displayName || adminData.email || userProfile.uid || "Admin";

      // Admin to Lead is AUTO-APPROVED
      await assignShift({
        userId,
        employeeId: employeeId || "",
        employeeName: userName || "",
        shiftId,
        shiftName: shift.name,
        shiftStart: shift.startTime,
        shiftEnd: shift.endTime,
        assignedBy: userProfile.uid,
        assignedByRole: "admin",
        teamLeadId: null,
        status: "APPROVED",
        approvedBy: adminIdentifier,
        reviewedBy: adminIdentifier,
      });

      setAssignState((s) => ({ ...s, [userId]: "" }));
      setAssignMsg(`Shift "${shift.name}" assigned to ${userName}.`);
      setTimeout(() => setAssignMsg(""), 4000);
    } catch (err) {
      console.error("[AdminShifts] assign error:", err);
      setAssignError(err.message || "Failed to assign shift.");
      setTimeout(() => setAssignError(""), 4000);
    }
    setAssigning((a) => ({ ...a, [userId]: false }));
  }

  async function handleReview(assignmentId, status) {
    setAssigning((a) => ({ ...a, [assignmentId]: true }));
    setAssignMsg("");
    setAssignError("");

    try {
      if (!userProfile?.uid) {
        throw new Error("Admin session not found. Please log in again.");
      }

      const adminRef = doc(db, "users", userProfile.uid);
      const adminSnap = await getDoc(adminRef);
      if (!adminSnap.exists()) {
        throw new Error("Admin profile not found in database.");
      }
      const adminData = adminSnap.data();
      if (adminData.role !== "admin") {
        throw new Error("Unauthorized. Only administrators can review shift assignments.");
      }

      const requestRef = doc(db, "shiftAssignments", assignmentId);
      const requestSnap = await getDoc(requestRef);
      if (!requestSnap.exists()) {
        throw new Error("The request document no longer exists.");
      }
      const requestData = requestSnap.data();

      if (status === "APPROVED") {
        const shiftId = requestData.shiftId;
        if (!shiftId) {
          throw new Error("Requested shift ID is missing in the request.");
        }
        const shiftRef = doc(db, "shifts", shiftId);
        const shiftSnap = await getDoc(shiftRef);
        if (!shiftSnap.exists()) {
          throw new Error("The requested shift no longer exists in database.");
        }
      }

      const adminIdentifier = adminData.fullName || adminData.displayName || adminData.email || userProfile.uid || "Admin";

      await reviewShiftAssignment(assignmentId, status, adminIdentifier, adminIdentifier);
      setAssignMsg(`Shift request ${status.toLowerCase()} successfully.`);
      setTimeout(() => setAssignMsg(""), 4000);
    } catch (err) {
      console.error("[AdminShifts] review error:", err);
      setAssignError(err.message || "Failed to review shift assignment.");
      setTimeout(() => setAssignError(""), 4000);
    }
    setAssigning((a) => ({ ...a, [assignmentId]: false }));
  }

  async function handleUnassign(assignmentId) {
    if (!window.confirm("Remove shift assignment?")) return;
    setAssigning((a) => ({ ...a, [assignmentId]: true }));
    try {
      await removeShiftAssignment(assignmentId);
    } catch (err) {
      console.error(err);
    }
    setAssigning((a) => ({ ...a, [assignmentId]: false }));
  }

  // ── Stats ────────────────────────────────────────────────

  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      <div className="page-header">
        <div>
          <h1 className="page-title"> Shift Management</h1>
        </div>
        {activeTab === "shifts" && (
          <button className="btn-primary" onClick={openAddShift}>
            + Add Shift
          </button>
        )}
      </div>

      {/* KPI row */}
      <div className="kpi-grid" style={{ marginBottom: "1.5rem" }}>
  
  <div className="kpi-card kpi-card--blue">
    <div className="kpi-icon">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="24"
        height="24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
        />
      </svg>
    </div>

    <div className="kpi-body">
      <span className="kpi-value">{shifts.length}</span>
      <span className="kpi-label">Shifts Defined</span>
    </div>
  </div>

  <div className="kpi-card kpi-card--green">
    <div className="kpi-icon">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="24"
        height="24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    </div>

    <div className="kpi-body">
      <span className="kpi-value">{assignedCount}</span>
      <span className="kpi-label">Employees Assigned</span>
    </div>
  </div>

  <div className="kpi-card kpi-card--amber">
    <div className="kpi-icon">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="24"
        height="24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    </div>

    <div className="kpi-body">
      <span className="kpi-value">{pendingRequests.length}</span>
      <span className="kpi-label">Pending Approval</span>
    </div>
  </div>

</div>

      {/* Messages */}
      {assignMsg && (
        <div
          style={{
            background: "var(--green-bg)",
            color: "var(--green-text)",
            border: "1px solid var(--green)",
            borderRadius: "var(--radius)",
            padding: "0.65rem 1rem",
            fontSize: "0.85rem",
            marginTop: "1rem",
            marginBottom: "0.5rem",
          }}
        >
          ✅ {assignMsg}
        </div>
      )}
      {assignError && (
        <div
          style={{
            background: "#fee2e2",
            color: "#991b1b",
            border: "1px solid #f87171",
            borderRadius: "var(--radius)",
            padding: "0.65rem 1rem",
            fontSize: "0.85rem",
            marginTop: "1rem",
            marginBottom: "0.5rem",
          }}
        >
          ❌ {assignError}
        </div>
      )}

      {/* Tab bar */}
      <div className="att-tab-bar">
  {[
    {
      key: "shifts",
      label: (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            width="18"
            height="18"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
            />
          </svg>

          <span>Shift Definitions</span>
        </>
      ),
    },

    {
      key: "assign",
      label: (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            width="18"
            height="18"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
            />
          </svg>

          <span>Assign to Leads</span>
        </>
      ),
    },

    {
      key: "requests",
      label: (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            width="18"
            height="18"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
            />
          </svg>

          <span>
            Pending Requests ({pendingRequests.length})
          </span>
        </>
      ),
    },
    {
      key: "report",
      label: (
        <>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="18" height="18">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
          </svg>
          <span>Assignment Report</span>
        </>
      ),
    },
  ].map(({ key, label }) => (
    <button
      key={key}
      className={`att-tab-btn ${
        activeTab === key ? "att-tab-btn--active" : ""
      }`}
      onClick={() => setActiveTab(key)}
    >
      {label}
    </button>
  ))}
</div>

      {/* ── TAB: SHIFT DEFINITIONS ── */}
      {activeTab === "shifts" && (
        <>
          {/* Add/Edit Modal */}
          {showShiftForm && (
            <div
              className="modal-overlay"
              onClick={() => setShowShiftForm(false)}
            >
              <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2 className="modal-title">
                    {editShift ? "Edit Shift" : "Create New Shift"}
                  </h2>
                  <button
                    className="modal-close"
                    onClick={() => setShowShiftForm(false)}
                  >
                    ✕
                  </button>
                </div>

                {/* Type selector */}
                <div className="form-group">
                  <label className="form-label">Shift Type</label>
                  <div
                    style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
                  >
                    {SHIFT_TYPES.map((t) => (
                      <button
                        key={t.value}
                        onClick={() => handleTypeChange(t.value)}
                        style={{
                          padding: "0.4rem 0.9rem",
                          borderRadius: "var(--radius)",
                          border: `2px solid ${shiftForm.type === t.value ? "var(--primary)" : "var(--border)"}`,
                          background:
                            shiftForm.type === t.value
                              ? "var(--primary-muted)"
                              : "var(--surface)",
                          cursor: "pointer",
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          color: "var(--text-primary)",
                        }}
                      >
                        {t.icon} {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Shift Name *</label>
                  <input
                    className="form-input"
                    placeholder="e.g. Morning Shift, Night Shift A…"
                    value={shiftForm.name}
                    onChange={(e) =>
                      setShiftForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                  }}
                >
                  <div className="form-group">
                    <label className="form-label">Start Time *</label>
                    <input
                      type="time"
                      className="form-input"
                      value={shiftForm.startTime}
                      onChange={(e) =>
                        setShiftForm((f) => ({
                          ...f,
                          startTime: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Time *</label>
                    <input
                      type="time"
                      className="form-input"
                      value={shiftForm.endTime}
                      onChange={(e) =>
                        setShiftForm((f) => ({ ...f, endTime: e.target.value }))
                      }
                    />
                  </div>
                </div>

                {shiftForm.startTime && shiftForm.endTime && (
                  <div
                    style={{
                      background: "var(--primary-muted)",
                      borderRadius: "var(--radius)",
                      padding: "0.5rem 0.75rem",
                      fontSize: "0.82rem",
                      marginBottom: "0.75rem",
                      color: "var(--primary)",
                    }}
                  >
                    ⏱ Duration:{" "}
                    {calcDuration(shiftForm.startTime, shiftForm.endTime)}
                    &nbsp;|&nbsp;
                    {fmt12(shiftForm.startTime)} → {fmt12(shiftForm.endTime)}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Description (optional)</label>
                  <textarea
                    className="form-input"
                    rows={2}
                    placeholder="Any notes about this shift…"
                    value={shiftForm.description}
                    onChange={(e) =>
                      setShiftForm((f) => ({
                        ...f,
                        description: e.target.value,
                      }))
                    }
                  />
                </div>

                {shiftError && <div className="form-error">{shiftError}</div>}

                <div className="modal-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => setShowShiftForm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleShiftSubmit}
                    disabled={shiftSaving}
                  >
                    {shiftSaving
                      ? "Saving…"
                      : editShift
                        ? "Update Shift"
                        : "Create Shift"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {sLoad ? (
            <div className="card-loading">Loading shifts…</div>
          ) : shifts.length === 0 ? (
            <div className="card-empty">
              No shifts defined yet.
              <br />
              <button
                className="btn-primary"
                style={{ marginTop: "1rem" }}
                onClick={openAddShift}
              >
                + Create First Shift
              </button>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              {shifts.map((s) => {
                const typeInfo =
                  SHIFT_TYPES.find((t) => t.value === s.type) || SHIFT_TYPES[3];
                const assignedTo = assignments.filter(
                  (a) => a.shiftId === s.id,
                ).length;
                return (
                  <div
                    key={s.id}
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-lg)",
                      padding: "1.25rem",
                      boxShadow: "var(--shadow-sm)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "1.5rem" }}>
                          {typeInfo.icon}
                        </div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: "1rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {s.name}
                        </div>
                        <span
                          className={`status-pill status-pill--${typeInfo.color}`}
                          style={{ marginTop: "0.25rem" }}
                        >
                          {typeInfo.label}
                        </span>
                      </div>
                      <div
                        className="action-btns"
                        style={{ flexDirection: "column", gap: "0.25rem" }}
                      >
                        <button
  className="btn-secondary-sm"
  onClick={() => openEditShift(s)}
  style={{
    display: "flex",
    alignItems: "center",
    gap: "6px",
  }}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    width="16"
    height="16"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
    />
  </svg>

  Edit
</button>
                        <button
  className="btn-danger-sm"
  onClick={() => handleDeleteShift(s.id)}
  disabled={deletingShift[s.id]}
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
  {deletingShift[s.id] ? (
    "…"
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="16"
      height="16"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
      />
    </svg>
  )}
</button>
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: "1rem",
                        fontSize: "0.85rem",
                        color: "var(--text-secondary)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span
  style={{
    display: "flex",
    alignItems: "center",
    gap: "6px",
  }}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    width="15"
    height="15"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
    />
  </svg>

  {fmt12(s.startTime)} → {fmt12(s.endTime)}
</span>
                        <span
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontWeight: 600,
                          }}
                        >
                          {calcDuration(s.startTime, s.endTime)}
                        </span>
                      </div>
                      {s.description && (
                        <div
                          style={{
                            marginTop: "0.5rem",
                            color: "var(--text-muted)",
                            fontSize: "0.8rem",
                          }}
                        >
                          {s.description}
                        </div>
                      )}
                      <div
                        style={{
                          marginTop: "0.75rem",
                          paddingTop: "0.75rem",
                          borderTop: "1px solid var(--border)",
                        }}
                      >
                        <span className="status-pill status-pill--blue">
                          {assignedTo} assigned
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── TAB: ASSIGN TO EMPLOYEES ── */}
      {activeTab === "assign" && (
        <>
          {uLoad || aLoad || sLoad ? (
            <div className="card-loading">Loading…</div>
          ) : leadsOnly.length === 0 ? (
            <div className="card-empty">No Team Leads found.</div>
          ) : (
            <div className="data-table-wrap" style={{ marginTop: "1rem" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Lead</th>
                    <th>Department</th>
                    <th>Current Shift</th>
                    <th>Assign New</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leadsOnly.map((emp) => {
                    // Filter assignments for this lead that are APPROVED
                    const current = localAssignments.find(a => a.userId === emp.uid && a.status?.toUpperCase() === 'APPROVED');
                    const selShift = assignState[emp.uid] ?? "";
                    const isAssigning = assigning[emp.uid];

                    return (
                      <tr key={emp.uid}>
                        <td>
                          <div className="table-user-cell">
                            <div className="table-avatar">
                              {emp.displayName?.slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {emp.displayName}
                              </div>
                              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                                {emp.employeeId}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{emp.department || "—"}</td>
                        <td>
                          {current ? (
                            <div>
                              <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                                {cleanShiftName(current.shiftName, current.shiftStart, current.shiftEnd)}
                              </div>
                              {(() => {
                                const sh = shifts.find(s => s.id === current.shiftId);
                                return sh ? (
                                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                                    {fmt12(sh.startTime)} → {fmt12(sh.endTime)}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          ) : (
                            <span style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>
                              Not assigned
                            </span>
                          )}
                        </td>
                        <td>
                          <select
                            className="filter-select"
                            value={selShift}
                            onChange={(e) =>
                              setAssignState((s) => ({ ...s, [emp.uid]: e.target.value }))
                            }
                            style={{ minWidth: "160px" }}
                          >
                            <option value="">Select shift…</option>
                            {shifts.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name} ({fmt12(s.startTime)}-{fmt12(s.endTime)})
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <div className="action-btns">
                            <button
                              className="btn-success-sm"
                              onClick={() => handleAssign(emp.uid, emp.displayName, emp.employeeId)}
                              disabled={!selShift || !!isAssigning}
                            >
                              {isAssigning ? "…" : "Assign"}
                            </button>
                            {current && (
                              <button
                                className="btn-danger-sm"
                                onClick={() => handleUnassign(current.id)}
                                disabled={!!isAssigning}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── TAB: PENDING REQUESTS ── */}
      {activeTab === "requests" && (
        <>
          {prLoad ? (
            <div className="card-loading">Loading requests…</div>
          ) : pendingRequests.length === 0 ? (
            <div className="card-empty">No pending shift requests.</div>
          ) : (
            <div className="data-table-wrap" style={{ marginTop: "1rem" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Requested Shift</th>
                    <th>Assigned By</th>
                    <th>Dept</th>
                    <th>Requested On</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRequests.map((req) => (
                    <tr key={req.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{req.employeeName}</div>
                        <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>{req.employeeId}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{cleanShiftName(req.shiftName, req.shiftStart, req.shiftEnd)}</div>
                        <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                          {fmt12(req.shiftStart)} - {fmt12(req.shiftEnd)}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{req.assignedByRole === 'admin' ? 'Admin' : 'Team Lead'}</div>
                      </td>
                      <td>{req.department || "—"}</td>
                      <td>{req.createdAt?.toDate ? req.createdAt.toDate().toLocaleDateString() : "—"}</td>
                      <td>
                        <div className="action-btns">
                          <button
                            className="btn-success-sm"
                            onClick={() => handleReview(req.id, "APPROVED")}
                            disabled={assigning[req.id]}
                          >
                            Approve
                          </button>
                          <button
                            className="btn-danger-sm"
                            onClick={() => handleReview(req.id, "REJECTED")}
                            disabled={assigning[req.id]}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {/* ── TAB: ASSIGNMENT REPORT ── */}
      {activeTab === "report" && (
        <>
          {/* Summary KPIs */}
          <div className="kpi-grid" style={{ marginTop: "1rem", marginBottom: "1.5rem" }}>
            <div className="kpi-card kpi-card--green">
              <div className="kpi-body">
                <span className="kpi-value">{assignedUsersList.length}</span>
                <span className="kpi-label">Assigned</span>
              </div>
            </div>
            <div className="kpi-card kpi-card--red">
              <div className="kpi-body">
                <span className="kpi-value">{unassignedUsersList.length}</span>
                <span className="kpi-label">Unassigned</span>
              </div>
            </div>
            <div className="kpi-card kpi-card--blue">
              <div className="kpi-body">
                <span className="kpi-value">{allActiveUsers.length}</span>
                <span className="kpi-label">Total Users</span>
              </div>
            </div>
          </div>

          {/* Shift-wise count */}
          <h3 style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.75rem" }}>
            Shift-wise Employee Count
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.75rem", marginBottom: "1.5rem" }}>
            {shifts.length === 0 ? (
              <div className="card-empty">No shifts defined.</div>
            ) : shifts.map((s) => {
              const typeInfo = SHIFT_TYPES.find((t) => t.value === s.type) || SHIFT_TYPES[3];
              return (
                <div 
                  key={s.id} 
                  onClick={() => setSelectedShiftForModal(s.id)}
                  style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "1rem", cursor: "pointer", transition: "transform 0.2s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                >
                  <div style={{ fontWeight: 700 }}>{s.name}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{fmt12(s.startTime)} → {fmt12(s.endTime)}</div>
                  <div style={{ marginTop: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span className={`status-pill status-pill--${typeInfo.color}`}>{typeInfo.label}</span>
                    <span style={{ fontWeight: 700, fontSize: "1.2rem", color: "var(--primary)" }}>{shiftWiseCounts[s.id] || 0}</span>
                    <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>employees</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Assigned Employees */}
          <h3 style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.75rem" }}>
            Assigned Employees
          </h3>
          {assignedUsersList.length === 0 ? (
            <div className="card-empty" style={{ marginBottom: "1.5rem" }}>No employees assigned yet.</div>
          ) : (
            <div className="data-table-wrap" style={{ marginBottom: "1.5rem" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th><th>Employee</th><th>ID</th><th>Department</th><th>Role</th><th>Shift</th><th>Timing</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedUsersList.map((u, idx) => {
                    const asgn = approvedAssignmentMap[u.uid];
                    const sh = shifts.find((s) => s.id === asgn?.shiftId);
                    return (
                      <tr key={u.uid}>
                        <td style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>{idx + 1}</td>
                        <td>
                          <div className="table-user-cell">
                            <div className="table-avatar">{u.displayName?.slice(0, 2).toUpperCase()}</div>
                            <div style={{ fontWeight: 600 }}>{u.displayName}</div>
                          </div>
                        </td>
                        <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem" }}>{u.employeeId || "—"}</td>
                        <td>{u.department || "—"}</td>
                        <td><span className={`status-pill status-pill--${u.role === "admin" ? "purple" : u.role === "lead" ? "blue" : "green"}`}>{u.role}</span></td>
                        <td style={{ fontWeight: 600 }}>{cleanShiftName(sh?.name || asgn?.shiftName || "—", sh?.startTime || asgn?.shiftStart, sh?.endTime || asgn?.shiftEnd)}</td>
                        <td style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>{fmt12(sh?.startTime || asgn?.shiftStart)} → {fmt12(sh?.endTime || asgn?.shiftEnd)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Unassigned Employees */}
          <h3 style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.75rem" }}>
            Unassigned Employees
          </h3>
          {unassignedUsersList.length === 0 ? (
            <div className="card-empty">✅ All active employees have a shift assigned.</div>
          ) : (
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th><th>Employee</th><th>ID</th><th>Department</th><th>Role</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {unassignedUsersList.map((u, idx) => (
                    <tr key={u.uid}>
                      <td style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>{idx + 1}</td>
                      <td>
                        <div className="table-user-cell">
                          <div className="table-avatar">{u.displayName?.slice(0, 2).toUpperCase()}</div>
                          <div style={{ fontWeight: 600 }}>{u.displayName}</div>
                        </div>
                      </td>
                      <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem" }}>{u.employeeId || "—"}</td>
                      <td>{u.department || "—"}</td>
                      <td><span className={`status-pill status-pill--${u.role === "admin" ? "purple" : u.role === "lead" ? "blue" : "green"}`}>{u.role}</span></td>
                      <td><span className="status-pill status-pill--red">No Shift</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Shift Employees Modal */}
          {selectedShiftForModal && (
            <div className="modal-overlay" onClick={() => setSelectedShiftForModal(null)}>
              <div className="modal-box" style={{ maxWidth: "800px" }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2 className="modal-title">
                    {shifts.find(s => s.id === selectedShiftForModal)?.name || "Shift"} Employees
                  </h2>
                  <button className="modal-close" onClick={() => setSelectedShiftForModal(null)}>✕</button>
                </div>
                <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto", padding: "1rem" }}>
                  {(() => {
                    const employeesInShift = assignedUsersList.filter(u => approvedAssignmentMap[u.uid]?.shiftId === selectedShiftForModal);
                    const shiftDetails = shifts.find(s => s.id === selectedShiftForModal);
                    
                    if (employeesInShift.length === 0) {
                      return <div className="card-empty">No employees assigned to this shift.</div>;
                    }
                    return (
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th>ID</th>
                            <th>Dept</th>
                            <th>Role</th>
                            <th>Type</th>
                            <th>Timing</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeesInShift.map(u => (
                            <tr key={u.uid}>
                              <td>
                                <div className="table-user-cell">
                                  <div className="table-avatar">{u.displayName?.slice(0, 2).toUpperCase()}</div>
                                  <div style={{ fontWeight: 600 }}>{u.displayName}</div>
                                </div>
                              </td>
                              <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem" }}>{u.employeeId || "—"}</td>
                              <td>{u.department || "—"}</td>
                              <td><span className={`status-pill status-pill--${u.role === "admin" ? "purple" : u.role === "lead" ? "blue" : "green"}`}>{u.role}</span></td>
                              <td>{u.employmentType || "—"}</td>
                              <td style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>
                                {fmt12(shiftDetails?.startTime)} → {fmt12(shiftDetails?.endTime)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
